import { useState } from "react";
import { useAuth } from "../lib/auth.jsx";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Loader2, Lock, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    const r = await login(email, password, rememberMe);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back!");
      navigate("/", { replace: true });
    } else {
      setErr(r.error);
    }
  };

  return (
    <div className="pds-login-bg min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-10 items-center">
        {/* Brand panel */}
        <div className="hidden md:block">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-md bg-red-600 text-white grid place-items-center font-display font-bold text-lg">PDS</div>
            <div>
              <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">Parameshwari Data Services</div>
              <div className="text-2xl font-display font-bold text-gray-900">Accounting Console</div>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold leading-tight text-gray-900">
            Track every <span className="pds-brand-gradient-text">rupee</span><br />
            from cable & internet collections.
          </h1>
          <p className="mt-5 text-base text-gray-600 max-w-md">
            A single dashboard for admin and field collectors —
            bill customers, record GPay / PhonePe / Cash payments,
            and watch your daily totals roll up in real time.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {["Internet", "Cable TV", "Office"].map((t, i) => (
              <div key={i} className="rounded-md border border-gray-200 bg-white/70 backdrop-blur-sm p-3 text-center">
                <div className="text-xs uppercase tracking-wider text-gray-500">{t}</div>
                <div className="font-display font-semibold text-red-700 mt-1">Tracked</div>
              </div>
            ))}
          </div>
        </div>

        {/* Login card */}
        <Card className="p-8 border-gray-200 shadow-md">
          <div className="md:hidden flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-md bg-red-600 text-white grid place-items-center font-display font-bold">PDS</div>
            <div className="font-display font-semibold">Parameshwari Data Services</div>
          </div>
          <h2 className="text-2xl font-display font-bold mb-1">Sign in</h2>
          <p className="text-sm text-gray-500 mb-6">Admin & Collector access</p>

          <form className="space-y-4" onSubmit={submit}>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="login-email-input"
                  id="email"
                  type="email"
                  className="pl-9"
                  placeholder="admin@pds.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  data-testid="login-password-input"
                  id="password"
                  type="password"
                  className="pl-9"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>
            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                data-testid="login-remember-me"
                checked={rememberMe}
                onCheckedChange={(v) => setRememberMe(Boolean(v))}
              />
              <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer">Remember me for 30 days</Label>
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-gray-500 leading-relaxed border-t pt-4">
            <div className="font-semibold mb-1 text-gray-700">Demo credentials</div>
            <div>Admin — <code>admin@pds.com</code> / <code>admin123</code></div>
            <div>Collector — <code>collector1@pds.com</code> / <code>collector123</code></div>
          </div>
        </Card>
      </div>
    </div>
  );
}
